'use client';

import Link from 'next/link';
import { JSX, useEffect, useState } from 'react';
import { BillingInfo, PurchasePreview, Subscription, SubscriptionStatus } from 'subscription-service-client';

import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Text,
    Tooltip,
} from '@videoblocks/design-system';
import { ChevronLeft as ChevronLeftIcon, InformationCircle, Lock } from '@videoblocks/react-icons';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import IconButton from '../../atoms/IconButton';
import { CreditCardDisplay } from '../CreditCardDisplay';
import { ActionResult, FinalPlan, PlanData, PlanPeriod } from './PlanTypes';
import { changeSubscriptionAction, previewChangeSubscription, restartSubscription } from './actions';
import { getCreditRenewalDate } from './getCreditRenewalDate';
import { isPlanDowngrade } from './isPlanDowngrade';

const logger = LoggerFactory.create('PlanConfirmationModal');

export interface PlanConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    plan: (PlanData | FinalPlan) | null;
    selectedPeriod: PlanPeriod;
    currencySymbol: string;
    currencyCode: string;
    subscriptionId: string;
    billingInfo: BillingInfo | null;
    onStatusUpdate: (result: ActionResult) => void;
    addOnPlanCodeSelected?: string;
    currentBillingAmount: number | null;
    subscriptionData?: Subscription | null;
}

export const formatPricingText = (amount: number, currencyCode: string, currencySymbol: string): string => {
    if (['CAD', 'AUD', 'USD'].includes(currencyCode)) {
        return `$${amount?.toFixed(2)} ${currencyCode}`;
    }

    return `${currencySymbol}${amount?.toFixed(2)} ${currencyCode}`;
};

export const PlanConfirmationModal = ({
    isOpen,
    onClose,
    plan,
    selectedPeriod,
    currencySymbol,
    currencyCode,
    subscriptionId,
    billingInfo,
    onStatusUpdate,
    addOnPlanCodeSelected,
    currentBillingAmount,
    subscriptionData,
}: PlanConfirmationModalProps): JSX.Element | null => {
    const [isLoading, setIsLoading] = useState(false);
    const [previewData, setPreviewData] = useState<PurchasePreview | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const targetPlanCode =
        (plan ? (selectedPeriod === 'monthly' ? plan.planCodes.monthly : plan.planCodes.annual) : '') ?? '';

    //? This is the qty of items we are requesting, could be more per the BE but business is enforcing 1 only for now
    const itemQty = 1;

    useEffect(() => {
        if (isOpen && plan && targetPlanCode) {
            setIsLoadingPreview(true);
            previewChangeSubscription(
                subscriptionId,
                targetPlanCode,
                addOnPlanCodeSelected && addOnPlanCodeSelected !== targetPlanCode
                    ? `${addOnPlanCodeSelected}:${itemQty}`
                    : undefined,
            )
                .then((data) => {
                    setPreviewData(data);
                    setIsLoadingPreview(false);
                })
                .catch((error) => {
                    setIsLoadingPreview(false);
                    // Don't close modal on preview error - let user proceed or close manually
                    logger.error({ error, subscriptionId, targetPlanCode }, 'Failed to load preview');
                });
        } else {
            setPreviewData(null);
            setIsLoadingPreview(false);
        }
        // eslint-disable-next-line
    }, [isOpen, plan, targetPlanCode, subscriptionId]);

    if (!plan) return null;

    const selectedCreditTier = addOnPlanCodeSelected
        ? plan?.creditTiers?.find((tier) => {
              const periodPlanCode = selectedPeriod === 'monthly' ? tier.monthlyPlanCode : tier.annualPlanCode;
              return periodPlanCode === addOnPlanCodeSelected;
          })
        : null;

    // Calculate monthly price (including base plan + addon if selected)
    const addOnsPrice = (selectedCreditTier?.additionalCost || 0) * (selectedPeriod === 'annual' ? 12 : 1);
    const basePlanPrice = (plan.pricing[selectedPeriod] || 0) * (selectedPeriod === 'annual' ? 12 : 1);
    // Calculate total price for the billing period (base plan + addon for annual)
    const totalPrice = selectedCreditTier ? basePlanPrice + addOnsPrice : basePlanPrice;

    // Check if this is a downgrade (needed for renewal date calculation)
    const isDowngradeForRenewal = isPlanDowngrade(currentBillingAmount, plan, selectedPeriod, addOnPlanCodeSelected);

    // Detect if upgrading from monthly to annual (not a downgrade)
    // For downgrades, changes take effect at term_end, so we show the subscription end date
    const isMonthlyToAnnualUpgrade =
        !isDowngradeForRenewal &&
        subscriptionData?.billingIntervalDays != null &&
        subscriptionData.billingIntervalDays <= 31 &&
        selectedPeriod === 'annual';

    // Calculate renewal date based on upgrade type
    // For monthly-to-annual upgrades, renewal is 1 year from today
    // For other cases (downgrades, same period changes), use the existing subscription end date
    const renewalDate = (() => {
        if (isMonthlyToAnnualUpgrade) {
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            return oneYearFromNow;
        }
        return subscriptionData?.subscriptionEndDate ? new Date(subscriptionData.subscriptionEndDate) : new Date();
    })();

    const formattedRenewalDate = renewalDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    // Check subscription status - separate checks for different UI behavior
    const isCancelled = subscriptionData?.status === SubscriptionStatus.Canceled;
    const isExpired = subscriptionData?.status === SubscriptionStatus.Expired;
    const isExpiredOrCancelled = isCancelled || isExpired;

    // Check if user is restarting their current plan (vs upgrading to different plan)
    const isRestartingCurrentPlan = subscriptionData?.plan?.code === targetPlanCode;

    const handleConfirmPurchase = async () => {
        setIsLoading(true);

        try {
            let result: ActionResult;

            if (isExpiredOrCancelled) {
                result = await restartSubscription(subscriptionId, targetPlanCode);
            } else {
                result = await changeSubscriptionAction(
                    subscriptionId,
                    targetPlanCode,
                    addOnPlanCodeSelected && addOnPlanCodeSelected !== targetPlanCode
                        ? [
                              {
                                  code: addOnPlanCodeSelected,
                                  quantity: 1,
                              },
                          ]
                        : undefined,
                );
            }

            onStatusUpdate(result);
        } catch (error) {
            onStatusUpdate({
                message: 'An unexpected error occurred. Please try again.',
                success: false,
            });
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    const planDisplayName = `${plan.name} (${selectedPeriod === 'annual' ? 'Annual' : 'Monthly'} Plan)`;
    const renewalInterval = selectedPeriod === 'annual' ? '/year' : '/month';

    const isDowngrade = isPlanDowngrade(currentBillingAmount, plan, selectedPeriod, addOnPlanCodeSelected);

    const credits = selectedCreditTier?.credits;
    const creditExpirationDate =
        (subscriptionData ? getCreditRenewalDate(subscriptionData) : renewalDate) || renewalDate;
    const formattedCreditExpirationDate = creditExpirationDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-[95vw] max-w-md mx-auto bg-white rounded-lg shadow-xl max-h-[80vh] overflow-y-auto mt-16 sm:mt-8 scrollbar-hidden gap-0 sm:max-w-md">
                <DialogDescription className="sr-only">
                    Review and confirm your plan update purchase details.
                </DialogDescription>
                <DialogHeader className="text-center flex-row justify-between">
                    <IconButton
                        onKeyDown={onClose}
                        onClick={onClose}
                        className={'w-fit text-blue-700 font-semibold'}
                        aria-label={'Go Back'}
                        title={'Go Back'}
                        as="button"
                    >
                        <div className="flex flex-nowrap items-center gap-2">
                            <ChevronLeftIcon className="w-4 h-4 fill-current" />
                            <span>Go Back</span>
                        </div>
                    </IconButton>
                </DialogHeader>

                <DialogTitle fontSize="2xl" className="text-gray-900 mt-4 mb-8">
                    Confirm your purchase
                </DialogTitle>

                <div className="space-y-6">
                    <div className="space-y-3">
                        {isCancelled && isRestartingCurrentPlan ? (
                            <div className="text-center">
                                <Text fontSize="base" fontWeight="regular" className="text-gray-900">
                                    Your subscription will restart today at{' '}
                                    <Text asChild fontWeight="semibold">
                                        <span>{formatPricingText(0, currencyCode, currencySymbol)}</span>
                                    </Text>
                                </Text>
                            </div>
                        ) : isExpiredOrCancelled ? (
                            <div className="text-center">
                                <Text fontSize="base" fontWeight="regular" className="text-gray-900">
                                    Your subscription will restart today at{' '}
                                    <Text asChild fontWeight="semibold">
                                        <span>
                                            {formatPricingText(totalPrice, currencyCode, currencySymbol)}
                                            {renewalInterval}
                                        </span>
                                    </Text>{' '}
                                    plus applicable local taxes.
                                </Text>
                            </div>
                        ) : isDowngrade ? (
                            <div className="text-center">
                                <Text fontSize="base" fontWeight="regular" className="text-gray-900">
                                    Your subscription will renew at{' '}
                                    <Text asChild fontWeight="semibold">
                                        <span>
                                            {formatPricingText(totalPrice, currencyCode, currencySymbol)}
                                            {renewalInterval}
                                        </span>
                                    </Text>{' '}
                                    plus applicable local taxes on {formattedRenewalDate}.
                                </Text>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <Text fontSize="sm" fontWeight="regular" className="text-gray-900">
                                        You will be billed{' '}
                                        <Text asChild fontSize="sm" fontWeight="semibold">
                                            <span>
                                                {isLoadingPreview
                                                    ? '-- --'
                                                    : formatPricingText(
                                                          previewData?.subtotal ?? totalPrice,
                                                          currencyCode,
                                                          currencySymbol,
                                                      )}
                                            </span>
                                        </Text>{' '}
                                        today for{' '}
                                        <Text asChild fontSize="sm" fontWeight="semibold">
                                            <span>{planDisplayName}</span>
                                        </Text>{' '}
                                        upgrade.
                                    </Text>
                                </div>
                                <div>
                                    <Text fontSize="sm" fontWeight="regular" className="text-gray-900">
                                        Your subscription will renew at{' '}
                                        <Text asChild fontSize="sm" fontWeight="semibold">
                                            <span>
                                                {formatPricingText(totalPrice, currencyCode, currencySymbol)}
                                                {renewalInterval}
                                            </span>
                                        </Text>{' '}
                                        on{' '}
                                        <Text asChild fontSize="sm" fontWeight="semibold">
                                            <span>{formattedRenewalDate}</span>
                                        </Text>
                                        .
                                    </Text>
                                </div>
                                {credits && (
                                    <div>
                                        <Text fontSize="sm" fontWeight="regular" className="text-gray-900">
                                            You will now have{' '}
                                            <Text asChild fontSize="sm" fontWeight="semibold">
                                                <span>{credits} Credits</span>
                                            </Text>{' '}
                                            that will expire on{' '}
                                            <Text asChild fontSize="sm" fontWeight="semibold">
                                                <span>{formattedCreditExpirationDate}</span>
                                            </Text>{' '}
                                        </Text>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Payment Method */}
                    {billingInfo && (
                        <div className="flex flex-col gap-4">
                            <Text fontSize="base" fontWeight="semibold" className="text-gray-900">
                                Payment Method
                            </Text>
                            <CreditCardDisplay billingInfo={billingInfo} />
                            <Text fontSize="sm" fontWeight="semibold" asChild>
                                <Link
                                    href={'/member/billing'}
                                    prefetch={false}
                                    className="text-blue-700 no-underline py-2"
                                >
                                    Edit Payment method
                                </Link>
                            </Text>
                        </div>
                    )}

                    {!isDowngrade && subscriptionData?.status === SubscriptionStatus.Active && (
                        <>
                            <div className="flex flex-col mt-4 mb-8">
                                <div className="flex justify-between items-center">
                                    <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        className="text-gray-900 flex items-center gap-1"
                                    >
                                        Subtotal (prorated)
                                    </Text>
                                    <Text fontSize="sm" fontWeight="regular" className="text-gray-900">
                                        {isLoadingPreview || !previewData
                                            ? '-- --'
                                            : formatPricingText(previewData?.subtotal, currencyCode, currencySymbol)}
                                    </Text>
                                </div>
                                <div className="flex justify-between items-center">
                                    <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        className="text-gray-900 flex items-center gap-1"
                                    >
                                        Estimated sales tax
                                        <span>
                                            <Tooltip
                                                title={
                                                    'Storyblocks is required to collect sales tax in certain tax jurisdictions per local law and regulations. Please contact customer support if you are a tax-exempt business'
                                                }
                                                trigger={<InformationCircle className="w-4 h-4 fill-gray-700" />}
                                            />
                                        </span>
                                    </Text>
                                    <Text fontSize="sm" fontWeight="regular" className="text-gray-900">
                                        {isLoadingPreview || !previewData
                                            ? '-- --'
                                            : formatPricingText(previewData?.tax, currencyCode, currencySymbol)}
                                    </Text>
                                </div>
                                <div className="flex justify-between items-center">
                                    <Text
                                        fontSize="sm"
                                        fontWeight="semibold"
                                        className="text-gray-900 flex items-center gap-1"
                                    >
                                        Total
                                    </Text>
                                    <Text fontSize="sm" fontWeight="regular" className="text-gray-900">
                                        {isLoadingPreview || !previewData
                                            ? '-- --'
                                            : formatPricingText(previewData?.total, currencyCode, currencySymbol)}
                                    </Text>
                                </div>
                            </div>
                            <div className="flex justify-between items-end mb-8">
                                <Text fontSize="lg" fontWeight="semibold" className="text-gray-900">
                                    Today&apos;s Total
                                </Text>
                                <Text fontSize="2xl" fontWeight="semibold" className="text-gray-900">
                                    {isLoadingPreview
                                        ? '-- --'
                                        : formatPricingText(
                                              previewData?.total ?? totalPrice,
                                              currencyCode,
                                              currencySymbol,
                                          )}
                                </Text>
                            </div>
                        </>
                    )}
                </div>
                {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                <Button onClick={handleConfirmPurchase} disabled={isLoading} className="w-full h-12">
                    {isLoading
                        ? 'Processing...'
                        : isExpiredOrCancelled
                          ? 'Restart Subscription'
                          : isDowngrade
                            ? 'Confirm Downgrade'
                            : 'Complete Payment'}
                </Button>

                <DialogFooter className="flex flex-col space-y-4">
                    <div className="text-center space-y-1 flex w-full justify-center items-center gap-1">
                        <Text fontSize="xs" fontWeight="regular" className="text-gray-700 m-0">
                            SSL Secure Payment
                        </Text>
                        <Lock className="w-4 h-4 fill-current" />
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
